import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ShieldCheck,
  Truck,
  BadgeCheck,
  Minus,
  Plus,
  Pill,
  Clock,
} from "lucide-react";
import Header from "../components/Header";
import Navbar from "../components/Navbar";
import ProductCard from "../components/ProductCard";
import TrustStrip from "../components/TrustStrip";
import { useShop } from "../context/ShopContext";
import { fetchProduct, fetchRelated } from "../api";
import { allProducts, categories, homeTrust } from "../mock";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useShop();
  const pid = Number(id);

  const mockProduct = allProducts.find((p) => p.id === pid);
  const [product, setProduct] = useState(mockProduct || null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(!mockProduct);

  useEffect(() => {
    let mounted = true;
    setQty(1);
    (async () => {
      try {
        const p = await fetchProduct(pid);
        if (mounted) setProduct(p);
      } catch {
        if (mounted && !mockProduct) setProduct(null);
      } finally {
        if (mounted) setLoading(false);
      }
      try {
        const r = await fetchRelated(pid);
        if (mounted) setRelated(r);
      } catch {
        if (mounted)
          setRelated(allProducts.filter((p) => p.id !== pid).slice(0, 4));
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  const cat = product && categories.find((c) => c.slug === product.category);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header variant="home" />
        <Navbar />
        <div className="max-w-[1200px] mx-auto px-4 py-20 text-center text-gray-400">
          Loading product...
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header variant="home" />
        <Navbar />
        <div className="max-w-[1200px] mx-auto px-4 py-20 text-center">
          <div className="text-[18px] font-bold text-gray-800">Product not found</div>
          <button
            onClick={() => navigate("/")}
            className="mt-4 bg-[#12a150] hover:bg-[#0d8a40] text-white text-[13px] font-semibold rounded-full px-6 py-2"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header variant="home" />
      <Navbar />

      <div className="max-w-[1200px] mx-auto px-3 lg:px-4 py-5">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-[11.5px] text-gray-500 mb-5">
          <Link to="/" className="hover:text-[#0e7a3d]">Home</Link>
          <ChevronRight size={12} />
          {cat && (
            <>
              <Link to={`/category/${cat.slug}`} className="hover:text-[#0e7a3d]">
                {cat.name}
              </Link>
              <ChevronRight size={12} />
            </>
          )}
          <span className="font-semibold text-gray-700 truncate">{product.name}</span>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
          {/* Image */}
          <div className="bg-[#f6faf7] rounded-2xl p-6 flex items-center justify-center relative">
            <span className="absolute top-4 left-4 bg-[#12a150] text-white text-[11px] font-bold px-2.5 py-1 rounded-full">
              {product.off}% OFF
            </span>
            <img
              src={product.img}
              alt={product.name}
              className="max-h-[320px] w-full object-contain rounded-xl"
            />
          </div>

          {/* Info */}
          <div>
            <div className="text-[12px] text-gray-400 font-medium">{product.brand}</div>
            <h1 className="text-[24px] sm:text-[28px] font-extrabold text-gray-800 leading-tight mt-1">
              {product.name}
            </h1>
            <div className="flex items-center gap-1.5 mt-2 text-[12px] text-[#0e7a3d] font-semibold">
              <BadgeCheck size={15} /> 100% Genuine · Verified by pharmacist
            </div>

            <div className="flex items-baseline gap-3 mt-4">
              <span className="text-[28px] font-extrabold text-[#0e7a3d]">₹ {product.price.toFixed(2)}</span>
              <span className="text-[15px] text-gray-400 line-through">₹ {product.mrp.toFixed(2)}</span>
              <span className="text-[12px] font-bold text-[#12a150] bg-green-50 px-2 py-0.5 rounded-full">
                Save ₹ {(product.mrp - product.price).toFixed(2)}
              </span>
            </div>
            <div className="text-[11px] text-gray-400 mt-1">Inclusive of all taxes</div>

            {product.description && (
              <p className="text-[13.5px] text-gray-600 leading-relaxed mt-5">
                {product.description}
              </p>
            )}

            {/* Qty + Add */}
            <div className="flex items-center gap-3 mt-6">
              <div className="flex items-center gap-3 border border-gray-200 rounded-full px-2 py-1.5">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-[#0e7a3d]"
                >
                  <Minus size={15} />
                </button>
                <span className="text-[15px] font-bold w-6 text-center">{qty}</span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-[#0e7a3d]"
                >
                  <Plus size={15} />
                </button>
              </div>
              <button
                onClick={() => addToCart(product, qty)}
                className="flex-1 bg-[#12a150] hover:bg-[#0d8a40] active:scale-[0.99] text-white text-[15px] font-bold rounded-full py-3 transition-[background-color,transform]"
              >
                Add to Cart
              </button>
            </div>

            {/* Dosage */}
            {product.dosage && (
              <div className="mt-6 bg-[#eef8f1] rounded-xl p-4">
                <div className="flex items-center gap-2 text-[13px] font-bold text-[#0e7a3d]">
                  <Pill size={16} /> Dosage & Usage
                </div>
                <p className="text-[13px] text-gray-600 mt-1.5 leading-relaxed">{product.dosage}</p>
              </div>
            )}

            {/* Perks */}
            <div className="grid grid-cols-3 gap-3 mt-6">
              {[
                { icon: ShieldCheck, t: "Genuine" },
                { icon: Truck, t: "Fast Delivery" },
                { icon: Clock, t: "Easy Returns" },
              ].map((p) => (
                <div key={p.t} className="flex flex-col items-center gap-1 text-center">
                  <p.icon size={20} className="text-[#12a150]" />
                  <span className="text-[11px] text-gray-600 font-medium">{p.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-[18px] font-bold text-gray-800 mb-4">You may also like</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5">
              {related.map((p) => (
                <ProductCard key={p.id} p={p} />
              ))}
            </div>
          </section>
        )}
      </div>

      <TrustStrip items={homeTrust} cols={4} />
    </div>
  );
};

export default ProductDetail;
